import { useEffect, useState } from 'react';

import LocalShippingIcon from '@mui/icons-material/LocalShipping';

import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';

import SwapHorizIcon from '@mui/icons-material/SwapHoriz';

import { warehouseApi, type WarehouseDashboardResponse } from '@/api/warehouse.api';

import { StatCard } from '@/components/ui/StatCard';

import { DashboardLayout } from '@/shared/layouts/DashboardLayout';

import { KpiGrid } from '@/components/ui/KpiGrid';

import { QuickActionGrid } from '@/components/ui/QuickActionGrid';

import { PageSection } from '@/components/ui/PageSection';

import { DashboardSkeleton } from '@/components/ui/Skeleton';

import { showApiError } from '@/utils/apiError';



const LINKS = [

  { to: '/warehouse/operations', label: 'Floor operations', description: 'Putaway, barcode scan, open tasks' },

  { to: '/warehouse/mobile', label: 'Mobile WMS', description: 'Handheld putaway, picks, scan' },

  { to: '/warehouse/picks', label: 'Pick / pack / dispatch', description: 'Create picks, confirm, ship' },

  { to: '/warehouse/transfers', label: 'Transfers', description: 'Inter-warehouse stock moves' },

  { to: '/warehouse/cycle-counts', label: 'Cycle counts', description: 'Count, variance approval, complete' },

  { to: '/warehouse/waves', label: 'Wave picking', description: 'Batch pick lists and release' },

  { to: '/warehouse/dock', label: 'Dock schedule', description: 'Inbound appointment calendar' },

  { to: '/warehouse/cross-dock', label: 'Cross-dock', description: 'Stage inbound to outbound' },

  { to: '/warehouse/labor', label: 'Labor metrics', description: 'Daily scans, picks, putaways' },

  { to: '/warehouse/setup', label: 'Setup', description: 'Warehouses, zones, bins' },

  { to: '/procurement/goods-receipts', label: 'Goods receipts', description: 'GRN triggers receiving workflow' },

  { to: '/pos/inventory', label: 'Inventory ledger', description: 'Stock balances (ledger-owned)' },

];



export function WarehouseDashboardPage() {

  const [stats, setStats] = useState<WarehouseDashboardResponse | null>(null);

  const [loading, setLoading] = useState(true);



  useEffect(() => {
    warehouseApi

      .dashboard()

      .then(setStats)

      .catch((e) => showApiError(e, 'Failed to load WMS dashboard'))

      .finally(() => setLoading(false));

  }, []);



  if (loading) {

    return (

      <DashboardLayout

        moduleLabel="Warehouse"

        title="Warehouse (WMS)"

        description="Physical movements and bin-level operations"

      >

        <DashboardSkeleton kpiCount={4} />

      </DashboardLayout>

    );

  }



  return (

    <DashboardLayout

      moduleLabel="Warehouse"

      title="Warehouse (WMS)"

      description="Physical movements and bin-level operations. Stock quantities update only via the Inventory ledger bridge."

    >

      <KpiGrid columns={4}>

        <StatCard title="Warehouses" value={stats?.warehouseCount ?? '—'} icon={<LocalShippingIcon />} />

        <StatCard title="Open putaway" value={stats?.openPutawayTasks ?? '—'} icon={<QrCodeScannerIcon />} />

        <StatCard title="Open picks" value={stats?.openPickLists ?? '—'} icon={<LocalShippingIcon />} />

        <StatCard title="Open transfers" value={stats?.openTransfers ?? '—'} icon={<SwapHorizIcon />} />

      </KpiGrid>



      <p className="text-sm text-slate-600 dark:text-slate-400">

        Movements today: <span className="font-semibold tabular-nums text-slate-900 dark:text-slate-100">{stats?.movementsToday ?? '—'}</span>

      </p>



      <PageSection title="Quick actions">

        <QuickActionGrid items={LINKS} columns={3} />

      </PageSection>

    </DashboardLayout>

  );

}


